import {
  EmailEditorVersion,
  hasOutlookWrap,
  hasUnscopedDataDetectorStyle,
  hasUnwrappedDividers,
  renderWaypointToHtml,
  sanitizeWaypointDocument,
} from "@simplyk/common";
import { TReaderDocument } from "@usewaypoint/email-builder";

interface SanitizeWaypointSubmitPayloadInput {
  body: string;
  designConfig: Record<string, unknown> | null | undefined;
  editorVersion: EmailEditorVersion | null | undefined;
}

interface SanitizeWaypointSubmitPayloadOutput<T> {
  sanitizedBody: string;
  sanitizedDesignConfig: T;
  didSanitize: boolean;
}

// Self-heal legacy email bodies with incompatible design configuration during save on Next from email body step.
export const sanitizeWaypointSubmitPayload = <T extends Record<string, unknown> | null | undefined>({
  body,
  designConfig,
  editorVersion,
}: SanitizeWaypointSubmitPayloadInput & { designConfig: T }): SanitizeWaypointSubmitPayloadOutput<T> => {
  if (!designConfig || editorVersion !== EmailEditorVersion.Waypoint) {
    return { sanitizedBody: body, sanitizedDesignConfig: designConfig, didSanitize: false };
  }
  const sanitizedDoc = sanitizeWaypointDocument(designConfig as unknown as TReaderDocument);
  const designConfigChanged = JSON.stringify(sanitizedDoc) !== JSON.stringify(designConfig);
  const bodyMissingOutlookWrap = !hasOutlookWrap(body);
  const hasCanvasBorderColorAndColumns = body.includes('class="ebc-col"') && /border\s*:\s*[\d.]+px\s+solid/.test(body);
  const bodyHasUnwrappedDividers = hasUnwrappedDividers(body);
  const bodyHasUnscopedDataDetectorStyle = hasUnscopedDataDetectorStyle(body);

  if (
    !designConfigChanged &&
    !bodyMissingOutlookWrap &&
    !hasCanvasBorderColorAndColumns &&
    !bodyHasUnwrappedDividers &&
    !bodyHasUnscopedDataDetectorStyle
  ) {
    return { sanitizedBody: body, sanitizedDesignConfig: designConfig, didSanitize: false };
  }

  const sanitizedBody = renderWaypointToHtml(sanitizedDoc);
  return {
    sanitizedBody,
    sanitizedDesignConfig: sanitizedDoc as unknown as T,
    didSanitize:
      designConfigChanged ||
      bodyMissingOutlookWrap ||
      bodyHasUnwrappedDividers ||
      bodyHasUnscopedDataDetectorStyle ||
      sanitizedBody !== body,
  };
};
